import { backofficeFetch } from '@/services/backoffice-api';
import type {
  AdjustBackofficeOrganizationBalanceInput,
  AdjustBackofficeOrganizationBalanceResponse,
  AddOrganizationMemberInput,
  AddOrganizationMemberResponse,
  BackofficeOrganizationDetailResponse,
  BackofficeOrganizationInterviewsResponse,
  BackofficeOrganizationMembersResponse,
  BackofficeOrganizationSalesHistoryResponse,
  BackofficeOrganizationSalesSummaryResponse,
  BackofficeOrganizationTransactionsResponse,
  BackofficeOrganizationsResponse,
  BackofficeSlugAvailabilityResponse,
  BackofficeTierTemplatesResponse,
  BackofficeUserLookupResponse,
  BackofficeUserSearchResponse,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  CreateOrganizationResponse,
  RemoveBackofficeOrganizationMemberResponse,
  TransferBackofficeOrganizationOwnershipResponse,
  UpdateBackofficeOrganizationFeaturesInput,
  UpdateBackofficeOrganizationFeaturesResponse,
  UpdateBackofficeOrganizationMemberRoleInput,
  UpdateBackofficeOrganizationMemberRoleResponse,
} from './types';

export async function fetchBackofficeOrganizations(
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeOrganizationsResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(`/organizations?${searchParams.toString()}`, init);

  if (!response.ok) {
    throw new Error(`Failed to load organizations (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationsResponse;
}

export async function fetchBackofficeUserByEmail(
  email: string,
  signal?: AbortSignal
): Promise<BackofficeUserLookupResponse | null> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const params = new URLSearchParams({ email });
  const response = await backofficeFetch(`/users/lookup?${params.toString()}`, init);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to resolve owner (${response.status})`);
  }

  return (await response.json()) as BackofficeUserLookupResponse;
}

export async function fetchBackofficeTierTemplates(
  signal?: AbortSignal
): Promise<BackofficeTierTemplatesResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch('/organizations/tier-templates', init);

  if (!response.ok) {
    throw new Error(`Failed to load tier templates (${response.status})`);
  }

  return (await response.json()) as BackofficeTierTemplatesResponse;
}

export async function checkBackofficeOrganizationSlugAvailability(
  slug: string,
  signal?: AbortSignal
): Promise<BackofficeSlugAvailabilityResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const params = new URLSearchParams({ slug });
  const response = await backofficeFetch(
    `/organizations/slug-availability?${params.toString()}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to validate slug (${response.status})`);
  }

  return (await response.json()) as BackofficeSlugAvailabilityResponse;
}

export async function searchBackofficeUsers(
  query: string,
  signal?: AbortSignal,
  organizationId?: string
): Promise<BackofficeUserSearchResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const params = new URLSearchParams({ query, limit: '10' });
  if (organizationId) params.set('organizationId', organizationId);
  const response = await backofficeFetch(`/users/search?${params.toString()}`, init);

  if (!response.ok) {
    throw new Error(`Failed to search users (${response.status})`);
  }

  return (await response.json()) as BackofficeUserSearchResponse;
}

export async function createBackofficeOrganization(
  input: CreateOrganizationInput
): Promise<CreateOrganizationResponse> {
  const response = await backofficeFetch('/organizations', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to create organization (${response.status})`);
  }

  return body as CreateOrganizationResponse;
}

export async function addBackofficeOrganizationMember(
  organizationId: string,
  input: AddOrganizationMemberInput
): Promise<AddOrganizationMemberResponse> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/members`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to add member (${response.status})`);
  }

  return body as AddOrganizationMemberResponse;
}

export async function manageBackofficeOrganizationInvitation(
  organizationId: string,
  invitationId: string,
  action: 'resend' | 'withdraw'
): Promise<void> {
  const path = `/organizations/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}`;
  const response = await backofficeFetch(action === 'resend' ? `${path}/resend` : path, {
    method: action === 'resend' ? 'POST' : 'DELETE',
    headers: { Accept: 'application/json' },
  });
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok)
    throw new Error(body?.error ?? `Failed to ${action} invitation (${response.status})`);
}

export async function fetchBackofficeOrganizationMembers(
  organizationId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeOrganizationMembersResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/members?${searchParams.toString()}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load organization members (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationMembersResponse;
}

export async function updateBackofficeOrganizationMemberRole(
  organizationId: string,
  subjectUserId: string,
  input: UpdateBackofficeOrganizationMemberRoleInput
): Promise<UpdateBackofficeOrganizationMemberRoleResponse> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(subjectUserId)}`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );
  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to update member role (${response.status})`);
  }

  return body as UpdateBackofficeOrganizationMemberRoleResponse;
}

export async function transferBackofficeOrganizationOwnership(
  organizationId: string,
  subjectUserId: string
): Promise<TransferBackofficeOrganizationOwnershipResponse> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(subjectUserId)}/transfer-ownership`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    }
  );
  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to transfer ownership (${response.status})`);
  }

  return body as TransferBackofficeOrganizationOwnershipResponse;
}

export async function removeBackofficeOrganizationMember(
  organizationId: string,
  subjectUserId: string
): Promise<RemoveBackofficeOrganizationMemberResponse> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(subjectUserId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
      },
    }
  );
  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to remove member (${response.status})`);
  }

  return body as RemoveBackofficeOrganizationMemberResponse;
}

export async function fetchBackofficeOrganizationDetail(
  organizationId: string,
  signal?: AbortSignal
): Promise<BackofficeOrganizationDetailResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load organization (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationDetailResponse;
}

export async function updateBackofficeOrganizationFeatures(
  organizationId: string,
  input: UpdateBackofficeOrganizationFeaturesInput
): Promise<UpdateBackofficeOrganizationFeaturesResponse> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/features`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to update organization features (${response.status})`);
  }

  return body as UpdateBackofficeOrganizationFeaturesResponse;
}

export async function fetchBackofficeOrganizationInterviews(
  organizationId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeOrganizationInterviewsResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/interviews?${searchParams.toString()}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load interviews (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationInterviewsResponse;
}

export async function fetchBackofficeOrganizationTransactions(
  organizationId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeOrganizationTransactionsResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/transactions?${searchParams.toString()}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load transactions (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationTransactionsResponse;
}

export async function fetchBackofficeOrganizationSalesSummary(
  organizationId: string,
  signal?: AbortSignal
): Promise<BackofficeOrganizationSalesSummaryResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/sales-summary`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load sales summary (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationSalesSummaryResponse;
}

export async function fetchBackofficeOrganizationSalesHistory(
  organizationId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeOrganizationSalesHistoryResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/sales-history?${searchParams.toString()}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load sales history (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationSalesHistoryResponse;
}

export async function adjustBackofficeOrganizationBalance(
  organizationId: string,
  input: AdjustBackofficeOrganizationBalanceInput
): Promise<AdjustBackofficeOrganizationBalanceResponse> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/transactions/adjust`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to adjust balance (${response.status})`);
  }

  return body as AdjustBackofficeOrganizationBalanceResponse;
}

export async function updateBackofficeOrganization(
  organizationId: string,
  input: UpdateOrganizationInput
): Promise<BackofficeOrganizationDetailResponse> {
  const response = await backofficeFetch(`/organizations/${encodeURIComponent(organizationId)}`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(body?.error ?? `Failed to update organization (${response.status})`);
  if (!body?.organization)
    throw new Error(
      'The organization was saved but its updated details could not be loaded. Refresh and review before retrying.'
    );
  return body as BackofficeOrganizationDetailResponse;
}

export async function fetchHostnameDefaults(
  organizationId: string,
  signal?: AbortSignal
): Promise<import('@nava/backoffice-bff-contracts').BackofficeHostnameDefaults> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/interview-hostname-defaults`,
    { method: 'GET', ...(signal ? { signal } : {}) }
  );
  if (!response.ok) throw new Error('Interview hostname defaults are temporarily unavailable');
  return response.json();
}

export async function updateHostnameDefaults(
  organizationId: string,
  defaults: { insights: string; customer_success: string }
): Promise<import('@nava/backoffice-bff-contracts').BackofficeHostnameDefaults> {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(organizationId)}/interview-hostname-defaults`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(defaults),
    }
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? 'Failed to update interview hostname defaults');
  }
  return response.json();
}
